import React from "react";
import copy from "copy-to-clipboard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "~/components/shared/DropdownMenu";
import Icon from "~/components/shared/Icon";
import { LogSettingsContext } from "~/components/shared/Log";
import JobLogLineStore from "~/stores/JobLogLineStore";
import classNames from "classnames";
import { Tooltip } from "~/components/shared/Tooltip";

type CopiedAction = "line" | "group" | "lines" | "permalink" | "range-permalink" | null;

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

/**
 * Dropdown menu for log line actions (copy line, copy lines, copy permalink).
 *
 * Always shows:
 * - "Copy line" — copies the text of the specific hovered line
 * - "Copy permalink" — copies a permalink to the specific hovered line
 *
 * Additionally, when the hovered line is inside a multi-line selected range:
 * - "Copy lines (L{start}-L{end})" — copies the text of all lines in the range
 * - "Copy permalink (L{start}-L{end})" — copies a permalink to the range
 *
 */
export function LogLineActionsDropdown({
  jobId,
  lineNumber,
  groupEndLine,
}: {
  jobId: string;
  lineNumber: number;
  groupEndLine?: number;
}) {
  const [copiedAction, setCopiedAction] = React.useState<CopiedAction>(null);
  const [open, setOpen] = React.useState(false);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const logSettings = React.useContext(LogSettingsContext);

  const isLineInRange = JobLogLineStore.isLineInRange(jobId, lineNumber);
  const range = isLineInRange ? JobLogLineStore.getHighlightedRange(jobId) : null;

  const triggerLabel = range
    ? `Actions for L${range.start}-L${range.end}`
    : `Actions for L${lineNumber}`;

  const handleOpenChange = React.useCallback((isOpen: boolean) => {
    if (isOpen) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
    }
    setOpen(isOpen);
    if (!isOpen) {
      setCopiedAction(null);
    }
  }, []);

  // Clean up timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  const copied = (action: CopiedAction) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setCopiedAction(action);
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      resetTimeoutRef.current = setTimeout(() => setCopiedAction(null), 150);
    }, 1500);
  };

  const handleCopyLine = React.useCallback(
    (event: Event) => {
      event.preventDefault();
      copy(JobLogLineStore.getTextForLines(jobId, lineNumber, lineNumber, logSettings));
      copied("line");
    },
    [jobId, lineNumber, logSettings],
  );

  const handleCopyLines = React.useCallback(
    (event: Event) => {
      event.preventDefault();
      copy(JobLogLineStore.getHighlightedLinesText(jobId, logSettings));
      copied("lines");
    },
    [jobId, logSettings],
  );

  const handleCopyPermalink = React.useCallback(
    (event: Event) => {
      event.preventDefault();
      const url = new URL(window.location.href);
      // Always pin permalinks to the specific job by `jid`. The current URL
      // may have `sid` (the step UUID) which silently re-resolves to whichever
      // job is latest under the step — so retrying the job changes which
      // execution the "permalink" points at.
      if (Features.BuildSidebar) {
        url.searchParams.delete("sid");
        url.searchParams.set("jid", jobId);
      }
      url.hash = Features.BuildSidebar ? `L${lineNumber}` : `${jobId}/L${lineNumber}`;
      copy(url.toString());
      copied("permalink");
    },
    [jobId, lineNumber],
  );

  const handleCopyGroup = React.useCallback(
    (event: Event) => {
      event.preventDefault();
      if (groupEndLine === undefined) {
        return;
      }
      copy(JobLogLineStore.getTextForLines(jobId, lineNumber, groupEndLine, logSettings));
      copied("group");
    },
    [jobId, lineNumber, groupEndLine, logSettings],
  );

  const handleCopyRangePermalink = React.useCallback(
    (event: Event) => {
      event.preventDefault();
      if (!range) {
        return;
      }
      const url = new URL(window.location.href);
      // See handleCopyPermalink: always use `jid` so the permalink stays
      // pinned to this specific job after retries.
      if (Features.BuildSidebar) {
        url.searchParams.delete("sid");
        url.searchParams.set("jid", jobId);
      }
      url.hash = Features.BuildSidebar
        ? `L${range.start}-L${range.end}`
        : `${jobId}/L${range.start}-L${range.end}`;
      copy(url.toString());
      copied("range-permalink");
    },
    [jobId, range],
  );

  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleEscapeKeyDown = React.useCallback((event: KeyboardEvent) => {
    event.stopPropagation();
  }, []);

  const handleCloseAutoFocus = React.useCallback((event: Event) => {
    event.preventDefault();
    triggerRef.current?.blur();
  }, []);

  const checkIcon = (action: CopiedAction) => (
    <Icon
      key={`check-${action}`}
      icon="heroicons/16/solid/check"
      className="animate-in zoom-in-50 mr-1.5 size-4 shrink-0 duration-150"
      style={{ color: "#22c55e" }}
    />
  );

  const copyIcon = (action: CopiedAction) =>
    copiedAction === action ? (
      checkIcon(action)
    ) : (
      <Icon icon="heroicons/outline/square-2-stack" className="mr-1.5 size-4 shrink-0" />
    );

  const linkIcon = (action: CopiedAction) =>
    copiedAction === action ? (
      checkIcon(action)
    ) : (
      <Icon icon="heroicons/16/solid/link" className="mr-1.5 size-4 shrink-0" />
    );

  const itemClassName = "rounded-none px-3 py-2 text-sm data-[highlighted]:bg-gray-100";
  const tooltip = (
    <>
      Line <span className="font-medium">{lineNumber}</span> actions
    </>
  );

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <Tooltip tooltip={tooltip} disabled={open}>
        <DropdownMenuTrigger asChild={true}>
          <button
            ref={triggerRef}
            type="button"
            aria-label={triggerLabel}
            onClick={stopPropagation}
            className={classNames(
              "flex h-5 w-5 items-center justify-center rounded bg-white/10 p-0 text-[color:var(--theme-text-1)] hover:bg-white/20 [[data-theme=solarized]_&]:bg-black/5 [[data-theme=solarized]_&]:hover:bg-black/10",
              open ? "!bg-white/20 [[data-theme=solarized]_&]:!bg-black/10" : "",
            )}
          >
            <Icon icon="heroicons/16/solid/ellipsis-horizontal" className="size-4" />
          </button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuPortal>
        <DropdownMenuContent
          align="start"
          side="bottom"
          className="px-0 py-1"
          onEscapeKeyDown={handleEscapeKeyDown}
          onCloseAutoFocus={handleCloseAutoFocus}
          onClick={stopPropagation}
        >
          <DropdownMenuItem className={itemClassName} onSelect={handleCopyLine}>
            {copyIcon("line")}
            Copy line <span className="ml-2 text-xs text-gray-800">L{lineNumber}</span>
          </DropdownMenuItem>
          {groupEndLine !== undefined && (
            <DropdownMenuItem className={itemClassName} onSelect={handleCopyGroup}>
              {copyIcon("group")}
              Copy group{" "}
              <span className="ml-2 text-xs text-gray-800">
                L{lineNumber}-L{groupEndLine}
              </span>
            </DropdownMenuItem>
          )}
          {range && (
            <DropdownMenuItem className={itemClassName} onSelect={handleCopyLines}>
              {copyIcon("lines")}
              Copy selected lines{" "}
              <span className="ml-2 text-xs text-gray-800">
                L{range.start}-L{range.end}
              </span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem className={itemClassName} onSelect={handleCopyPermalink}>
            {linkIcon("permalink")}
            Copy permalink <span className="ml-2 text-xs text-gray-800">L{lineNumber}</span>
          </DropdownMenuItem>
          {range && (
            <DropdownMenuItem className={itemClassName} onSelect={handleCopyRangePermalink}>
              {linkIcon("range-permalink")}
              Copy permalink{" "}
              <span className="ml-2 text-xs text-gray-800">
                L{range.start}-L{range.end}
              </span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
